import { useCallback, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

/* ------------------------------------------------------------------ */
/*  useSendWhatsAppReminder (PRD.221, rewired by PRD.300)             */
/*                                                                    */
/*  Thin client for `psendAppointmentReminder` — the ONE shared       */
/*  reminder service also used by the 06:00 batch and the same-day    */
/*  trigger, so all three dedupe against each other.                  */
/*                                                                    */
/*  The backend owns eligibility, phone resolution, opt-in checks,    */
/*  template resolution, parameter mapping, the dated client          */
/*  reference, provider sending and retries. This hook sends an       */
/*  appointment id and nothing else.                                  */
/* ------------------------------------------------------------------ */

const REMINDER_URL = import.meta.env.VITE_APPOINTMENT_REMINDER_URL;

export type SendReminderStatus = 'idle' | 'sending' | 'success' | 'error';

export interface SendReminderInput {
  /**
   * Appointment id — the ONLY thing the client supplies. The backend re-reads
   * the patient, phone, times and template, and computes the dated dedupe
   * reference itself (PRD.300).
   */
  appointmentId: string;
  /** 'manual' (default) bypasses the automatic lead-time floor. */
  trigger?: 'manual' | 'same_day';
}

/** Shape returned by psendAppointmentReminder. */
export interface SendReminderResult {
  appointmentId: string;
  /**
   * `sent` — the provider accepted it.
   * `queued` — persisted; the retry worker will deliver it.
   * `skipped` — deliberately not sent (see `reason`).
   * `pending` — appointment has not synced to Postgres yet.
   *
   * Note none of these mean "delivered to the patient's handset": delivery
   * receipts land later in notification_deliveries.
   */
  status: 'sent' | 'queued' | 'skipped' | 'pending';
  reason?: string;
  jobId?: string;
  clientReference?: string;
  accepted?: boolean;
  errorMessage?: string;
}

export interface UseSendWhatsAppReminderReturn {
  status: SendReminderStatus;
  error: string | null;
  lastResult: SendReminderResult | null;
  /** True when the function endpoint is configured (VITE_WHATSAPP_NOTIFICATIONS_URL set). */
  enabled: boolean;
  /** Submit a reminder. Returns the result, or null on error. */
  send: (input: SendReminderInput) => Promise<SendReminderResult | null>;
  reset: () => void;
}

export function useSendWhatsAppReminder(): UseSendWhatsAppReminderReturn {
  const [status, setStatus] = useState<SendReminderStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SendReminderResult | null>(null);

  const enabled = Boolean(REMINDER_URL);

  const send = useCallback(async (input: SendReminderInput): Promise<SendReminderResult | null> => {
    if (!REMINDER_URL) {
      setError('WhatsApp reminder endpoint is not configured for this client.');
      setStatus('error');
      return null;
    }

    setStatus('sending');
    setError(null);

    try {
      const token = await getIdToken();
      const res = await fetch(REMINDER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // Appointment identity only. No template id, no phone, no params, and
        // no client reference — the backend owns every one of those.
        body: JSON.stringify({
          appointmentId: input.appointmentId,
          trigger: input.trigger ?? 'manual',
        }),
      });

      const body: unknown = await res.json().catch(() => null);

      if (!res.ok) {
        setError(friendlyError(res.status, body));
        setStatus('error');
        return null;
      }

      const result = body as SendReminderResult;
      setLastResult(result);
      setStatus('success');
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send WhatsApp reminder.';
      setError(msg);
      setStatus('error');
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setLastResult(null);
  }, []);

  return { status, error, lastResult, enabled, send, reset };
}

/* ------------------------------------------------------------------ */
/*  Map backend status + error payloads to actionable UI copy.        */
/* ------------------------------------------------------------------ */

function friendlyError(httpStatus: number, body: unknown): string {
  const raw =
    typeof body === 'object' && body !== null && 'error' in body
      ? String((body as { error?: unknown }).error ?? '')
      : '';

  switch (httpStatus) {
    case 400:
      return raw || 'The reminder request was invalid. Check the patient phone number.';
    case 401:
      return 'Your session expired. Please sign in again.';
    case 403:
      return raw || 'This patient has not opted in to WhatsApp messaging.';
    case 404:
      return raw || 'The APPOINTMENT_REMINDER template is not configured for this clinic.';
    case 409:
      return raw || 'The APPOINTMENT_REMINDER template is inactive.';
    default:
      return raw || `Failed to send WhatsApp reminder (HTTP ${httpStatus}).`;
  }
}

/* ------------------------------------------------------------------ */
/*  Outcome wording (PRD.300)                                          */
/*                                                                     */
/*  Deliberately precise: "sent" means the provider ACCEPTED it, not   */
/*  that the patient received it — delivery receipts land later in     */
/*  notification_deliveries. Claiming delivery here would be wrong.    */
/* ------------------------------------------------------------------ */

export function reminderOutcomeMessage(result: SendReminderResult, patientName: string): string {
  switch (result.status) {
    case 'sent':
      return `WhatsApp reminder sent to ${patientName}.`;
    case 'queued':
      return 'Reminder queued — the provider has not accepted it yet.';
    case 'pending':
      return 'Reminder pending — the appointment has not finished syncing.';
    case 'skipped':
      return reminderSkipMessage(result.reason, patientName);
  }
}

function reminderSkipMessage(reason: string | undefined, patientName: string): string {
  switch (reason) {
    case 'already_dispatched':
      return `A reminder has already been sent to ${patientName} today.`;
    case 'not_opted_in':
      return `${patientName} has not opted in to WhatsApp messages.`;
    case 'opted_out':
      return `${patientName} has opted out of WhatsApp messages.`;
    case 'no_phone':
      return 'No valid mobile number on file for this patient.';
    case 'appointment_in_past':
      return 'This appointment has already started — no reminder sent.';
    case 'not_today':
      return 'Reminders are sent on the day of the appointment.';
    case 'status_terminal':
      return 'This appointment is cancelled or closed — no reminder sent.';
    case 'template_missing':
    case 'template_inactive':
    case 'template_not_whatsapp_meta':
    case 'template_mapping_invalid':
      return 'WhatsApp reminders are not configured for this clinic.';
    default:
      return 'Reminder was not sent.';
  }
}
